"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { iconMap, DiscordButtonIcon } from "@/lib/iconMap";
import { usePathname } from "next/navigation";
import { resolveI18nText, NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";

const buttonVariants = cva("button cta", {
  variants: {
    variant: {
      default: "button-default",
      white: "button-white",
      outline: "button-outline",
      ghost: "button-ghost",
      "outline-black": "button-outline-black",
      green: "button-green",
      discord: "button-discord",
      darkGlow: "button-dark-glow",
      yellow: "button-yellow",
      defaultOldDesign: "button-default-old-design",
      lightGreenOldDesign: "button-light-green-old-design",
    },
    size: {
      default: "",
      sm: "button-size-small",
      lg: "button-size-large",
      defaultOld: "button-size-default-old-design",
    },
    shape: {
      square: "button-shape-square",
      round: "button-shape-round",
      pill: "button-shape-pill",
    },
    action: {
      link: "",
      popup: "",
      anchor: "",
      none: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    shape: "round",
    action: "none",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, shape, action, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn("group", buttonVariants({ variant, size, shape, className }))}
        data-type={variant}
        data-size={size}
        data-shape={shape}
        data-action={action}
        ref={ref}
        {...props}>
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQButton;
  overrideText?: string;
  language?: string;
  overrideVariant?:
    | "default"
    | "white"
    | "outline"
    | "ghost"
    | "outline-black"
    | "green"
    | "discord"
    | "darkGlow"
    | "yellow"
    | "defaultOldDesign"
    | "lightGreenOldDesign";
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, data, overrideText, overrideVariant, language, ...props }, ref) => {
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const pathname = usePathname();
    const segments = pathname.split("/").filter(Boolean);
    const currentLanguage =
      stegaClean(language) || (segments.find((s) => NON_DEFAULT_LANGUAGES.includes(s)) ?? DEFAULT_LANGUAGE);

    const type = (overrideVariant ?? cleanedData?.type ?? "default") as
      | "default"
      | "white"
      | "outline"
      | "ghost"
      | "outline-black"
      | "green"
      | "discord"
      | "darkGlow"
      | "defaultOldDesign"
      | "lightGreenOldDesign";

    const isDiscord = type === "discord";
    const isDarkGlow = type === "darkGlow";
    const isGlow = isDarkGlow;

    const size = (cleanedData?.size ?? "default") as "sm" | "default" | "lg";
    const shape = (cleanedData?.shape ?? "round") as "square" | "round" | "pill";

    const hasIcon = cleanedData?.cta?.icon ?? "none";
    const icon = iconMap[hasIcon as keyof typeof iconMap];
    // WhatsApp reads as a platform badge: larger, and sitting before the label.
    const isWhatsapp = hasIcon === "whatsapp";

    const action = cleanedData?.cta?.action ?? null;

    const buttonElement = (
      <Button
        className="group relative flex w-full items-center justify-center gap-[1.25rem] overflow-clip"
        variant={type}
        size={size}
        shape={shape}
        action={action ?? "none"}
        ref={ref}
        {...props}>
        {isGlow ? (
          <div className="button-bg absolute top-0 left-0 h-full w-full" />
        ) : (
          <>
            <div className="button-bg absolute top-0 left-0 h-full w-full transition-transform duration-500 group-hover:-translate-y-full" />
            <div className="button-bg-hover absolute top-0 left-0 h-full w-full translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
          </>
        )}

        <div className="relative overflow-clip leading-none">
          <div className="button-text transition-transform duration-500 group-hover:-translate-y-full">
            {data?.cta
              ? resolveI18nText(data.cta.title, data.cta.i18n, currentLanguage) || "Please add a title"
              : "Please select a CTA"}
          </div>
          <div className="button-text-hover absolute top-0 left-0 translate-y-full transition-transform duration-500 group-hover:translate-y-0">
            {data?.cta
              ? resolveI18nText(data.cta.title, data.cta.i18n, currentLanguage) || "Please add a title"
              : "Please select a CTA"}
          </div>
        </div>

        {isDiscord && (
          <div className="relative z-10 flex shrink-0 items-center">
            <DiscordButtonIcon />
          </div>
        )}

        {isGlow && (
          <div className="relative z-10 h-[2.5rem] w-[2.5rem] shrink-0 overflow-clip">
            <div className="transition-transform duration-500 group-hover:translate-x-full">
              <svg
                className="h-[2.5rem] w-[2.5rem]"
                viewBox="0 0 15 12.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <title>Arrow right</title>
                <path
                  d="M8.75 0L7.85625 0.870625L12.5937 5.625H0V6.875H12.5937L7.85625 11.6081L8.75 12.5L15 6.25L8.75 0Z"
                  fill="#e2f2f3"
                />
              </svg>
            </div>
            <div className="absolute top-0 left-0 -translate-x-full transition-transform duration-500 group-hover:translate-x-0 group-hover:delay-200">
              <svg
                className="h-[2.5rem] w-[2.5rem]"
                viewBox="0 0 15 12.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <title>Arrow right</title>
                <path
                  d="M8.75 0L7.85625 0.870625L12.5937 5.625H0V6.875H12.5937L7.85625 11.6081L8.75 12.5L15 6.25L8.75 0Z"
                  fill="#e2f2f3"
                />
              </svg>
            </div>
          </div>
        )}

        {icon && !isDiscord && !isGlow && (
          <div
            className={cn(
              "relative z-10 shrink-0 overflow-clip",
              isWhatsapp
                ? "order-first h-[3.5rem] w-[3.5rem]"
                : "h-[2.5rem] w-[2.5rem] [&_svg]:h-[2.5rem] [&_svg]:w-[2.5rem]",
            )}>
            <div className="transition-transform duration-500 group-hover:translate-x-full">{icon}</div>
            <div className="absolute top-0 left-0 -translate-x-full transition-transform duration-500 group-hover:translate-x-0 group-hover:delay-200">
              {icon}
            </div>
          </div>
        )}
      </Button>
    );

    return (
      <CTAWrapper
        cta={cleanedData?.cta}
        className={cn("cta-wrapper flex items-center relative z-10 w-full sm:w-auto gap-[.5rem]", className)}>
        {buttonElement}
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = "CTAButton";

export { Button, buttonVariants, CTAButton };
